import { nextTick } from "vue";
import videojs from "video.js";
import "videojs-quality-selector-hls";
import { usePlayerStore } from "@/store/player";

const FOUR_K = 2160;

const formatLabel = (text) => (text.startsWith(String(FOUR_K)) ? "4K" : text);

const parseQuality = (item) => {
  const text = item.querySelector(".vjs-menu-item-text")?.innerText ?? "";

  if (text === "Auto") {
    return "auto";
  }

  if (text.toLowerCase() === "4k") {
    return FOUR_K;
  }

  return parseInt(text.match(/\d+/)?.[0] ?? "0", 10);
};

const relabelMenu = (wrapper) => {
  wrapper?.querySelectorAll(".vjs-quality-selector .vjs-menu-item-text").forEach((el) => {
    el.innerText = formatLabel(el.innerText);
  });
};

// Safari plays HLS natively, which exposes no quality levels: read the variants from the
// master playlist and switch the source to the chosen variant playlist ("Auto" = master)
const addNativeHlsLevels = async (player, selector, masterSrc) => {
  const body = await fetch(masterSrc)
    .then((response) => (response.ok ? response.text() : ""))
    .catch(() => "");
  const lines = body.split(/\r?\n/);
  const variants = [];

  lines.forEach((line, i) => {
    const resolution = line.startsWith("#EXT-X-STREAM-INF") && line.match(/RESOLUTION=(\d+)x(\d+)/);
    const uri = lines[i + 1]?.trim();

    if (resolution && uri && !uri.startsWith("#")) {
      variants.push({ width: +resolution[1], height: +resolution[2], src: new URL(uri, masterSrc).href });
    }
  });

  if (player.isDisposed() || !variants.length) {
    return;
  }

  const setQuality = selector.setQuality.bind(selector);
  let currentSrc = masterSrc;

  selector.setQuality = (quality) => {
    setQuality(quality);

    const src = variants.find((variant) => Math.min(variant.width, variant.height) === quality)?.src ?? masterSrc;

    if (src === currentSrc) {
      return;
    }

    const time = player.currentTime();
    const paused = player.paused();

    currentSrc = src;
    player.src({ src, type: "application/vnd.apple.mpegurl" });
    player.one("loadedmetadata", () => {
      player.currentTime(time);

      if (!paused) {
        player.play()?.catch(() => {});
      }
    });
  };

  variants.forEach((variant, i) => {
    let enabled = true;

    player.qualityLevels().addQualityLevel({
      id: String(i),
      width: variant.width,
      height: variant.height,
      enabled: (value) => {
        if (value !== undefined) {
          enabled = value;
        }

        return enabled;
      },
    });
  });
};

export default (player, videoWrapper, source) => {
  if (videojs.browser.IS_IOS) {
    return;
  }

  const playerStore = usePlayerStore();
  const selector = player.qualitySelectorHls({ displayCurrentQuality: true });
  const setButtonText = selector.setButtonInnerText.bind(selector);

  selector.setButtonInnerText = (text) => setButtonText(formatLabel(text));

  player.qualityLevels().on("addqualitylevel", () => nextTick(() => relabelMenu(videoWrapper)));

  const applyStoredQuality = () => {
    relabelMenu(videoWrapper);

    const stored = playerStore.getQuality;
    const quality = stored === "auto" ? "auto" : parseInt(stored, 10);
    const items = [...(videoWrapper?.querySelectorAll(".vjs-quality-selector .vjs-menu-item") ?? [])];

    items.forEach((item) => {
      item.addEventListener("click", () => playerStore.setQuality(parseQuality(item)));
    });

    const current = items.find((item) => parseQuality(item) === quality);

    if (!current) {
      return;
    }

    videoWrapper.querySelector(".vjs-quality-selector .vjs-selected")?.classList.remove("vjs-selected");
    current.classList.add("vjs-selected");
    selector.setQuality(quality);
  };

  if (!videojs.browser.IS_ANY_SAFARI) {
    player.one("loadedmetadata", applyStoredQuality);
    return;
  }

  if (!source?.includes(".m3u8")) {
    return;
  }

  // after ready: the plugin builds its button and subscribes to addqualitylevel there
  player.ready(async () => {
    await addNativeHlsLevels(player, selector, source);
    await nextTick();
    applyStoredQuality();
  });
};
